import React, { useMemo, useState } from 'react';
import { Merge, Pencil, Trash2, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { DeviceTag } from '../types';
import { TagStillInUse, deleteTag, mergeTags, renameTag, useTagVocabulary } from '../hooks/useDeviceTags';
import { normalizeTagName, tagNameError } from '../utils/deviceTags';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ConfirmDialog';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';

/**
 * FS-501 — manage tags.
 *
 * The cleanup valve, and the piece that actually holds the vocabulary down
 * over time: typeahead and the create confirmation slow proliferation, but
 * merging duplicates is what fixes it, in one action across every device.
 *
 * This screen deliberately has NO create action. A tag can only be born by
 * being attached to a device, so the typeahead stays the single birth path —
 * a create button here would be a second one, and the list of existing tags
 * is exactly the context in which inventing another looks harmless.
 */

type Busy = { id: number; what: string } | null;
const NO_MERGE_TARGET = '__none__';

const TagsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  // Rename, merge and delete change a tag on EVERY device carrying it,
  // including devices outside this operator's entity scope, so they need a
  // global permission rather than the scoped write_devices. `manage_tags`
  // grants exactly these three actions and nothing else, so vocabulary
  // cleanup no longer costs a full system_admin grant; system_admin still
  // carries them. Anyone with read_devices can still see the vocabulary.
  const canEdit = hasPermission('manage_tags') || hasPermission('system_admin');
  const { tags, loading, error, reload } = useTagVocabulary();

  const [busy, setBusy] = useState<Busy>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; value: string } | null>(null);
  const [merging, setMerging] = useState<{ id: number; into: string } | null>(null);
  /**
   * Delete confirmation. `deviceCount` is the AUTHORITATIVE fleet-wide count
   * the server returns when it refuses an unforced delete — not the vocabulary
   * listing's count, which is narrowed to this operator's scope and can read
   * zero for a tag that is on devices they cannot see.
   */
  const [confirmDelete, setConfirmDelete] = useState<
    { tag: DeviceTag; deviceCount: number } | null
  >(null);

  const sorted = useMemo(() => [...tags].sort((a, b) => a.name.localeCompare(b.name)), [tags]);

  /** First pass: ask the server to delete, unforced, and let it tell us. */
  const askDelete = async (tag: DeviceTag) => {
    setBusy({ id: tag.id, what: 'delete' });
    setActionError(null);
    try {
      await deleteTag(tag.id, false);
      reload();
    } catch (err: any) {
      if (err instanceof TagStillInUse) {
        setConfirmDelete({ tag, deviceCount: err.deviceCount });
      } else {
        setActionError(err?.response?.data?.message || err?.message || 'Failed to delete tag');
      }
    } finally {
      setBusy(null);
    }
  };

  const run = async (id: number, what: string, fn: () => Promise<unknown>) => {
    setBusy({ id, what });
    setActionError(null);
    try {
      await fn();
      setRenaming(null);
      setMerging(null);
      setConfirmDelete(null);
      reload();
    } catch (err: any) {
      // Show what the server actually said — a merge or delete refusal
      // carries the reason, and hiding it just makes the operator guess.
      setActionError(err?.response?.data?.message || err?.message || `Failed to ${what} tag`);
    } finally {
      setBusy(null);
    }
  };

  const renameError = renaming ? tagNameError(renaming.value) : null;
  const renameClashes =
    renaming &&
    !renameError &&
    tags.some((t) => t.id !== renaming.id && normalizeTagName(t.name) === normalizeTagName(renaming.value));
  const renameUnchanged = renaming && normalizeTagName(renaming.value) === normalizeTagName(tags.find((tag) => tag.id === renaming.id)?.name ?? '');

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-1">Tags</h1>
      <p className="text-sm text-muted-foreground mb-5">
        Tags are created by adding them to a device, on the Device List or a device&rsquo;s Overview
        tab. Use this page to tidy the list up: rename a tag everywhere, merge two that mean the
        same thing, or delete one you are done with.
      </p>

      {error && <p className="mb-4 text-sm text-red-700">{error}</p>}
      {actionError && !confirmDelete && (
        <p className="mb-4 p-3 rounded-sm border border-status-error-border bg-red-50 text-sm text-red-800" role="alert">
          {actionError}
        </p>
      )}

      {loading && tags.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading tags…</p>
      ) : sorted.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No tags yet. Add one to a device and it will appear here.
        </p>
      ) : (
        <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Tag</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600 w-32">Devices</th>
                {canEdit && <th className="text-right px-4 py-2 font-medium text-gray-600 w-64">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map((tag) => {
                const isBusy = busy?.id === tag.id;
                return (
                  <tr key={tag.id} className="border-b last:border-b-0">
                    <td className="px-4 py-2">
                      {renaming?.id === tag.id ? (
                        <div>
                          <div className="flex items-center gap-1">
                            <Label htmlFor={`rename-tag-${tag.id}`} className="text-xs">New name</Label>
                            <Input
                              id={`rename-tag-${tag.id}`}
                              autoFocus
                              value={renaming.value}
                              onChange={(e) => {
                                setRenaming({ id: tag.id, value: e.target.value });
                                setActionError(null);
                              }}
                              className="w-56"
                              aria-label={`New name for ${tag.name}`}
                              aria-describedby={[`rename-tag-help-${tag.id}`, renameError ? `rename-tag-error-${tag.id}` : null, renameClashes ? `rename-tag-clash-${tag.id}` : null].filter(Boolean).join(' ')}
                              aria-invalid={!!(renameError || renameClashes) || undefined}
                            />
                            <Button
                              variant="icon"
                              disabled={isBusy || !!renameUnchanged}
                              aria-describedby={renameUnchanged ? `rename-tag-unchanged-${tag.id}` : undefined}
                              onClick={() => {
                                if (renameError || renameClashes) {
                                  setActionError(renameError || 'That tag already exists — merge the two instead.');
                                  return;
                                }
                                void run(tag.id, 'rename', () => renameTag(tag.id, renaming.value));
                              }}
                              aria-label="Save new name"
                              title="Rename this tag. Every device that carries it shows the new name."
                            >
                              <Check size={16} aria-hidden />
                            </Button>
                            <Button
                              variant="icon"
                              onClick={() => setRenaming(null)}
                              aria-label="Cancel rename"
                              title="Keep the current name"
                            >
                              <X size={16} aria-hidden />
                            </Button>
                          </div>
                          <p id={`rename-tag-help-${tag.id}`} className="mt-1 text-xs text-muted-foreground">Renaming changes this tag on every device that carries it.</p>
                          {renameUnchanged && <p id={`rename-tag-unchanged-${tag.id}`} className="mt-1 text-xs text-muted-foreground">Nothing to save: the name has not changed.</p>}
                          {renameError && <p id={`rename-tag-error-${tag.id}`} className="mt-1 text-xs text-amber-700">{renameError}</p>}
                          {renameClashes && (
                            <p id={`rename-tag-clash-${tag.id}`} className="mt-1 text-xs text-amber-700">
                              That tag already exists — merge the two instead.
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-sm border bg-slate-50 border-border text-slate-700">
                          {tag.name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                      {tag.device_count ?? 0}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-2">
                        {merging?.id === tag.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Select value={merging.into || NO_MERGE_TARGET} onValueChange={(next) => setMerging({ id: tag.id, into: next === NO_MERGE_TARGET ? '' : next ?? '' })}>
                              <SelectTrigger aria-label={`Merge ${tag.name} into`} title="Choose the tag that will replace this one" aria-describedby={`merge-tag-help-${tag.id}`}>
                                <SelectValue>{merging.into ? sorted.find((target) => String(target.id) === merging.into)?.name : 'Merge into…'}</SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NO_MERGE_TARGET}>Merge into…</SelectItem>
                                {sorted.filter((target) => target.id !== tag.id).map((target) => (
                                  <SelectItem key={target.id} value={String(target.id)}>{target.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span id={`merge-tag-help-${tag.id}`} className="sr-only">Choose which existing tag receives every device from {tag.name}.</span>
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => {
                                if (!merging.into) {
                                  setActionError('Choose a destination tag before merging.');
                                  return;
                                }
                                void run(tag.id, 'merge', () => mergeTags(tag.id, Number(merging.into)));
                              }}
                              title="Move every device on this tag to the tag chosen on the left, then remove this tag."
                            >
                              Merge
                            </Button>
                            <Button
                              variant="icon"
                              onClick={() => setMerging(null)}
                              aria-label="Cancel merge"
                              title="Stop merging and leave both tags as they are."
                            >
                              <X size={16} aria-hidden />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="icon"
                              size="md"
                              disabled={isBusy}
                              onClick={() => setRenaming({ id: tag.id, value: tag.name })}
                              aria-label={`Rename ${tag.name}`}
                              title="Rename this tag on every device that carries it"
                            >
                              <Pencil size={16} aria-hidden />
                            </Button>
                            <Button
                              variant="icon"
                              size="md"
                              disabled={isBusy || sorted.length < 2}
                              onClick={() => setMerging({ id: tag.id, into: '' })}
                              aria-label={`Merge ${tag.name}`}
                              title="Move every device on this tag to another tag, then remove this one"
                            >
                              <Merge size={16} aria-hidden />
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={isBusy}
                              onClick={() => void askDelete(tag)}
                              aria-label={`Delete ${tag.name}`}
                              title="Delete this tag and remove it from every device"
                            >
                              <Trash2 size={16} aria-hidden />
                            </Button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog
        open={confirmDelete !== null}
        error={confirmDelete ? actionError : null}
        title={`Delete ${confirmDelete?.tag.name ?? 'tag'}?`}
        message={confirmDelete ? `Remove from ${confirmDelete.deviceCount} device${confirmDelete.deviceCount === 1 ? '' : 's'} and delete?` : undefined}
        tone="danger"
        confirmLabel="Delete"
        confirmTitle="Take this tag off every device that carries it and delete the tag"
        submitting={busy?.what === 'delete'}
        onConfirm={() => {
          if (confirmDelete) void run(confirmDelete.tag.id, 'delete', () => deleteTag(confirmDelete.tag.id, true));
        }}
        onCancel={() => {
          setConfirmDelete(null);
          setActionError(null);
        }}
      />
    </div>
  );
};

export default TagsPage;
